import React, { useContext, useEffect, useState } from 'react';
import { IconButton, Badge } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { ROUTES } from 'shared/routes';
import { pluralize } from 'humanize-plus';
import NotificationContext from 'shared/contexts/NotificationContext';
import { onCreateNotification } from 'graphql/subscriptions';
import NotificationService from 'services/notification';
import UserContext from 'shared/contexts/UserContext';
import { client } from 'lib/graphql-client';

function notificationsLabel(count: number) {
  if (count === 0) {
    return 'no notifications';
  }
  if (count > 50) {
    return 'more than 50 notifications';
  }

  return `${count} ${pluralize(count, 'notification')}`;
}

const NotificationIcon: React.FC = () => {
  const [userState] = useContext(UserContext);
  const { person } = userState;
  const [count, setCount] = useState(0);
  const { updatedCount } = useContext(NotificationContext);
  const location = useLocation();

  useEffect(() => {
    // With every new notification, increment the count.
    const subscription = (client.graphql({
      query: onCreateNotification,
      variables: { personID: person.id },
    }) as any).subscribe({
      next: () => setCount(count + 1),
      // eslint-disable-next-line no-console
      error: (error: any) => console.warn(error),
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [count]);

  useEffect(() => {
    // A component has updated the count of unread notifications,
    // either because notifcations have been viewed or the user
    // has used "mark all read."
    if (typeof updatedCount === 'number') {
      setCount(updatedCount);
    }
  }, [updatedCount]);

  useEffect(() => {
    // Initial app load. Get the current count of unread notifications.
    let ignore = false;
    const update = async () => {
      const newCount = await NotificationService.getUnreadCount(person.id);
      if (!ignore) {
        setCount(newCount);
      }
    };

    update();

    return () => { ignore = true; };
  }, [person?.id]);

  if (!person?.id) {
    return null;
  }

  return (
    <IconButton
      aria-label={notificationsLabel(count)}
      component={Link}
      to={ROUTES.notifications}
      disabled={location.pathname === ROUTES.notifications}
      size="large">
      <Badge badgeContent={count} max={50} color="primary">
        <NotificationsNoneIcon color={count > 0 ? 'primary' : 'disabled'} />
      </Badge>
    </IconButton>
  );
};

export default NotificationIcon;
